import { useEffect, useLayoutEffect, useState } from "react";
import { Banknote, Check, Clock, CreditCard, QrCode, SmartphoneNfc, User, X } from "lucide-react";
import BillingMirrorHeader from "../BillingMirrorHeader";
import type { PaymentMirrorCommand } from "../types";
import BhimLogo from "@assets/images/bhim_logo.png";
import UpiLogo from "@assets/images/upi_logo.png";
import BhimIcon from "@assets/images/bhim-logo.png";
import GpayIcon from "@assets/images/google-pay-icon.png";
import PhonepeIcon from "@assets/images/phonepe-icon.svg";
import PaytmLogo from "@assets/images/paytm_logo.png";

export interface IMirrorBillingSummary {
  customer_name: string;
  line_items_count: number;
  total_mrp: number;
  discount: number;
  total_tax: number;
  invoice_amount: number;
  total_payable: number;
  is_advance?: boolean;
}

export const QR_TIMEOUT_SECONDS = 600;

const PAY_OPTIONS = [
  { icon: QrCode, label: "Scan this QR with any UPI app", allowed: true },
  { icon: CreditCard, label: "Card on Vetic's card machine", allowed: true },
  { icon: Banknote, label: "Cash at the reception", allowed: true },
  { icon: SmartphoneNfc, label: "Never to a personal UPI ID or number", allowed: false },
];

const UPI_APPS = [
  { logo: GpayIcon, label: "Google Pay" },
  { logo: PhonepeIcon, label: "PhonePe" },
  { logo: PaytmLogo, label: "Paytm" },
  { logo: BhimIcon, label: "BHIM" },
];

const formatAmount = (value: number) =>
  `₹${value.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const BillingSummaryComponent = ({
  summary,
  mappedClinicName,
  payment,
  onPaymentEnd,
}: {
  summary: IMirrorBillingSummary | null;
  mappedClinicName: string;
  payment: PaymentMirrorCommand | null;
  onPaymentEnd: () => void;
}) => {
  const [secondsLeft, setSecondsLeft] = useState(QR_TIMEOUT_SECONDS);

  useLayoutEffect(() => {
    if (payment?.action !== "QR_SHOW") return;
    const deadline = Date.now() + (payment.expiresIn ?? QR_TIMEOUT_SECONDS) * 1000;
    const tick = () => setSecondsLeft(Math.floor((deadline - Date.now()) / 1000));
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [payment]);

  useEffect(() => {
    if (payment?.action === "QR_SHOW" && secondsLeft <= 0) onPaymentEnd();
  }, [secondsLeft]);

  const rows = summary
    ? [
        ...(summary.is_advance
          ? []
          : [
              { label: "Total MRP", value: formatAmount(summary.total_mrp) },
              { label: "Discount", value: `- ${formatAmount(summary.discount)}` },
              { label: "Total Tax", value: formatAmount(summary.total_tax) },
              { label: "Invoice Amount", value: formatAmount(summary.invoice_amount) },
            ]),
        ...(payment?.overdueAmount > 0 ? [{ label: "Overdue Amount", value: formatAmount(payment.overdueAmount) }] : []),
        ...(payment?.advanceAmount > 0 ? [{ label: "Advance Payment", value: formatAmount(payment.advanceAmount) }] : []),
      ]
    : [];

  const isPaidWithoutQR = !payment && summary?.total_payable <= 0;

  if (payment?.action === "PAYMENT_SUCCESS" || isPaidWithoutQR) {
    const details = [
      ...(isPaidWithoutQR ? [] : [{ label: "Payment mode", value: "UPI" }]),
      ...(summary ? [{ label: "Paid by", value: `${summary.customer_name}` }] : []),
    ];
    return (
      <div className="flex flex-col bg-[#F6F7FA] p-4 h-[var(--mirror-frame-height,100vh)]">
        <BillingMirrorHeader clinicName={mappedClinicName} />
        <div className="flex flex-1 items-center justify-center">
          <div className="flex w-full max-w-[760px] flex-col items-center rounded-3xl border border-gray-200 bg-white px-14 py-12 shadow-[0_20px_50px_-20px_rgba(15,23,42,0.15)]">
            <div className="flex h-28 w-28 items-center justify-center rounded-full bg-green-50">
              <Check className="h-14 w-14 text-green-700" strokeWidth={3} />
            </div>
            <p className="mt-6 text-4xl font-semibold text-slate-900">Payment Successful</p>
            <p className="mt-6 text-lg text-slate-500">Amount paid</p>
            <p className="text-5xl font-bold text-slate-900">₹{(isPaidWithoutQR ? summary.invoice_amount : payment.amount).toLocaleString("en-IN")}</p>
            <div className="mt-8 w-full space-y-4 rounded-2xl border border-gray-200 bg-[#F6F7FA] px-6 py-5 text-lg">
              {details.map((row) => (
                <div key={row.label} className="flex justify-between gap-6">
                  <span className="shrink-0 whitespace-nowrap text-slate-500">{row.label}</span>
                  <span className="whitespace-nowrap font-medium text-slate-900">{row.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const isInactive = payment?.action === "QR_INACTIVE";

  const summaryCard = summary && (
    <div className="w-full max-w-[970px] rounded-3xl border border-gray-200 bg-white shadow-[0_20px_50px_-20px_rgba(15,23,42,0.15)] overflow-hidden" style={{ zoom: 0.6 }}>
      <div className="flex items-center justify-between px-12 py-6">
        <h2 className="text-[42px] font-bold text-slate-900">{summary.is_advance ? "Advance Collection" : "Bill Generated"}</h2>
        {isInactive && (
          <span className="rounded-full bg-gray-200 px-5 py-2 text-lg font-semibold tracking-widest text-slate-700">PAYMENT CANCELLED</span>
        )}
      </div>
      <div className="border-y border-gray-200 bg-[#F8F9FB]">
        <div className="flex items-center gap-5 px-11 py-7">
          <div className="flex h-[76px] w-[76px] shrink-0 items-center justify-center rounded-full bg-indigo-50">
            <User className="h-8 w-8 text-indigo-500" />
          </div>
          <div className="min-w-0">
            <p className="text-lg tracking-widest text-slate-500">Name</p>
            <p className="truncate text-[30px] font-medium text-slate-900">{summary.customer_name}</p>
          </div>
        </div>
      </div>
      <div className="px-12 py-9">
        <div className="flex items-center justify-between mb-6">
          <p className="text-lg font-semibold tracking-widest text-slate-500">PRICE BREAKUP</p>
        </div>
        <div className="space-y-6">
          {rows.map((row) => (
            <div
              key={row.label}
              className={`flex items-center justify-between text-[30px] ${row.label === "Invoice Amount" ? "border-t-2 border-dashed border-gray-300 pt-6" : ""}`}
            >
              <span className={row.label === "Overdue Amount" ? "text-red-400" : "text-slate-500"}>{row.label}</span>
              <span className={row.label === "Overdue Amount" ? "text-red-400" : "text-slate-900"}>{row.value}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="flex items-center justify-between border-t border-gray-200 bg-[#F8F9FB] px-12 py-8">
        <span className="text-[32px] text-slate-900">Total Amount Payable</span>
        <span className="text-[56px] font-bold text-slate-900">
          ₹{(payment?.amount ?? summary.total_payable).toLocaleString("en-IN")}
        </span>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col bg-[#F6F7FA] p-4 h-[var(--mirror-frame-height,100vh)] overflow-y-auto">
      <BillingMirrorHeader clinicName={mappedClinicName} />
      <div className="flex flex-1 items-start gap-6 p-6">
        {summaryCard && <div className="w-[36%] max-w-[582px] shrink-0">{summaryCard}</div>}
        {!payment ? (
          <div className="flex flex-1 flex-col items-center justify-center self-stretch rounded-3xl border border-gray-200 bg-white p-8 text-center">
            <div className="flex items-center justify-center rounded-2xl border-2 border-dashed border-slate-300 p-4">
              <QrCode className="h-[300px] w-[300px] text-blue-600 opacity-60 blur-[6px]" />
            </div>
            <p className="mt-8 text-4xl font-semibold text-slate-900">Your QR is on its way</p>
            <p className="mt-4 max-w-[640px] text-2xl text-slate-600">The Clinic Manager will generate your payment QR shortly.</p>
          </div>
        ) : isInactive ? (
          <div className="flex flex-1 flex-col items-center justify-center self-stretch rounded-3xl border border-gray-200 bg-white p-8 text-center">
            <div className="relative flex items-center justify-center rounded-2xl border-2 border-dashed border-slate-300 p-4">
              <img src={payment.qrBase64} alt="Inactive QR" className="h-[300px] w-[300px] opacity-60 blur-[6px]" />
              <span className="absolute -rotate-12 rounded-xl border-2 border-slate-900 bg-white px-6 py-3 text-2xl font-bold tracking-widest text-slate-900">
                QR INACTIVE
              </span>
            </div>
            <p className="mt-8 text-4xl font-semibold text-slate-900">{secondsLeft <= 0 ? "QR Expired" : "Payment Mode Switched!"}</p>
            <p className="mt-4 max-w-[640px] text-2xl text-slate-600">
              Still want to pay through UPI? Please ask the Clinic Manager to generate the QR again.
            </p>
          </div>
        ) : (
        <div className="flex flex-1 flex-col items-center justify-center self-stretch rounded-3xl border border-gray-200 bg-white p-8">
          {summary?.customer_name && <p className="mb-3 max-w-full truncate text-4xl font-bold text-slate-900">{summary.customer_name}</p>}
          <p className="text-4xl font-semibold text-slate-900">
            Scan & Pay <span className="text-[#E8456B]">₹{payment.amount.toLocaleString("en-IN")}</span> here
          </p>
          <div className="mt-6 flex flex-col items-center rounded-3xl border-2 border-slate-900 px-8 py-5 shadow-[0_20px_50px_-20px_rgba(15,23,42,0.25)]">
            <div className="flex items-center justify-center gap-6">
              <img src={BhimLogo} alt="BHIM" className="h-11" />
              <span className="h-9 w-px bg-gray-300" />
              <img src={UpiLogo} alt="UPI" className="h-11" />
            </div>
            <img src={payment.qrBase64} alt="Payment QR" className="mt-3 h-[400px] w-[400px]" />
            <div className="mt-4 flex w-full flex-col items-center border-t border-dashed border-gray-300 pt-4">
              <p className="text-base font-semibold tracking-widest text-slate-500">SCAN & PAY WITH ANY UPI APP</p>
              <div className="mt-3 flex items-center justify-center gap-7">
                {UPI_APPS.map(({ logo, label }) => (
                  <img key={label} src={logo} alt={label} className="h-10 w-auto object-contain" />
                ))}
              </div>
            </div>
          </div>
          <p className="mt-6 flex items-center gap-2 text-xl text-slate-700">
            <Clock className="h-6 w-6" /> QR expires in
            <b className="text-3xl text-slate-900">
              {String(Math.floor(Math.max(secondsLeft, 0) / 60)).padStart(2, "0")}:{String(Math.max(secondsLeft, 0) % 60).padStart(2, "0")}
            </b>
          </p>
        </div>
        )}
      </div>
      <div className="flex items-center gap-5 px-6 pb-4">
        <p className="shrink-0 text-xl font-semibold text-slate-900">Only pay using</p>
        {PAY_OPTIONS.map(({ icon: Icon, label, allowed }) => (
          <div
            key={label}
            className={`relative flex flex-1 items-center gap-4 rounded-xl border p-4 text-lg font-medium text-slate-900 ${allowed ? "border-gray-200 bg-white" : "border-red-300 bg-red-50"}`}
          >
            <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-lg ${allowed ? "bg-green-50 text-green-700" : "bg-red-100 text-red-600"}`}>
              <Icon className="h-6 w-6" />
            </div>
            {label}
            <span className={`absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full text-white ${allowed ? "bg-green-700" : "bg-red-600"}`}>
              {allowed ? <Check className="h-4 w-4" /> : <X className="h-4 w-4" />}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default BillingSummaryComponent;
